"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AI_CONFIG_TRACKED_FIELDS = exports.AiRegistryConfigRowSchema = exports.AiConfigHistoryEntrySchema = exports.AiConfigChangeSchema = exports.CONFIG_HISTORY_LIMIT = exports.MAX_TOKENS_PER_CREDIT = exports.MAX_AI_MULTIPLIER = exports.DEFAULT_AI_MULTIPLIER = exports.DEFAULT_USD_PER_RUBY_TOKEN = exports.USD_PER_STORED_CREDIT = void 0;
exports.tokensPerCreditFor = tokensPerCreditFor;
exports.validateUsdPerRubyToken = validateUsdPerRubyToken;
exports.validateDefaultMultiplier = validateDefaultMultiplier;
exports.resolveTokenEconomics = resolveTokenEconomics;
exports.appendConfigHistory = appendConfigHistory;
/**
 * Ruby Token economics — the owner-editable rate (Super Admin → AI → Settings).
 *
 * Owner 2026-10-17: "1 Ruby Token = $0.00001, so 1,000 tokens = 1 credit =
 * $0.01 — move it to settings", and "the multiplier is changeable".
 *
 * Storage never changes: wallets, reservations and ledgers hold CREDITS and
 * 1 credit stays $0.01. The setting is the USD value of ONE Ruby Token; from
 * it follow tokens-per-credit (= 0.01 / x) — used to DISPLAY balances and to
 * convert token-denominated settings (action min/max, tier allowances) into
 * credits. A balance keeps its dollar value when the rate changes.
 *
 * Rule: 0.01 / x must be a whole number ≥ 1, so credits ↔ tokens stays exact
 * (0.00001 → 1,000; 0.000001 → 10,000; 0.0001 → 100).
 *
 * The values live on the AI registry CONFIG row (pk 'AI', sk 'CONFIG') with
 * an audit history. Every reader falls back to the defaults below when the
 * row is missing or a field is invalid — charging must never break on config.
 *
 * RUNTIME values — import by NAME from '@platform/shared-types'.
 */
const zod_1 = require("zod");
/** One stored credit, in USD. Fixed: it is the storage unit (no migration). */
exports.USD_PER_STORED_CREDIT = 0.01;
/** Default value of ONE Ruby Token, in USD (owner rule). */
exports.DEFAULT_USD_PER_RUBY_TOKEN = 0.00001;
/** Default multiplier on vendor cost when an action / item has none of its own. */
exports.DEFAULT_AI_MULTIPLIER = 1.5;
/** Same ceiling as an action's own multiplier (AiActionRowSchema). */
exports.MAX_AI_MULTIPLIER = 50;
/** Tokens per credit may not exceed this (x ≥ $0.00000001). */
exports.MAX_TOKENS_PER_CREDIT = 1000000;
/** How many audit entries the CONFIG row keeps (newest last). */
exports.CONFIG_HISTORY_LIMIT = 50;
/**
 * Whole tokens per stored credit for a token value, or null when the value
 * is not allowed (≤ 0, non-finite, above $0.01, past the cap, or 0.01 / x not
 * a whole number). Float wobble (0.01 / 0.00001 = 999.999…) is tolerated.
 */
function tokensPerCreditFor(usdPerToken) {
    if (typeof usdPerToken !== 'number' || !Number.isFinite(usdPerToken) || usdPerToken <= 0)
        return null;
    const q = exports.USD_PER_STORED_CREDIT / usdPerToken;
    const n = Math.round(q);
    if (n < 1 || n > exports.MAX_TOKENS_PER_CREDIT)
        return null;
    if (Math.abs(q - n) > 1e-6 * Math.max(1, n))
        return null;
    return n;
}
function validateUsdPerRubyToken(x) {
    if (typeof x !== 'number' || !Number.isFinite(x) || x <= 0) {
        return { ok: false, error: 'Enter a dollar value above $0 for one Ruby Token.' };
    }
    if (x > exports.USD_PER_STORED_CREDIT) {
        return { ok: false, error: 'One Ruby Token cannot be worth more than one credit ($0.01).' };
    }
    const n = tokensPerCreditFor(x);
    if (n === null) {
        return {
            ok: false,
            error: '$0.01 divided by this value must be a whole number of tokens (e.g. $0.00001 = 1,000 per credit, $0.000001 = 10,000), so credits and tokens convert exactly.',
        };
    }
    return { ok: true, tokensPerCredit: n };
}
function validateDefaultMultiplier(m) {
    if (typeof m !== 'number' || !Number.isFinite(m) || m <= 0 || m > exports.MAX_AI_MULTIPLIER) {
        return { ok: false, error: `The default multiplier must be above 0 and at most ${exports.MAX_AI_MULTIPLIER}.` };
    }
    return { ok: true };
}
/** Never throws. Invalid or missing fields fall back field by field. */
function resolveTokenEconomics(row) {
    const r = row && typeof row === 'object' && !Array.isArray(row) ? row : {};
    const stored = typeof r.usdPerRubyToken === 'number' ? r.usdPerRubyToken : NaN;
    const perCredit = tokensPerCreditFor(stored);
    const usdPerRubyToken = perCredit !== null ? stored : exports.DEFAULT_USD_PER_RUBY_TOKEN;
    const tokensPerCredit = perCredit ?? tokensPerCreditFor(exports.DEFAULT_USD_PER_RUBY_TOKEN);
    const m = typeof r.defaultMultiplier === 'number' ? r.defaultMultiplier : NaN;
    return {
        usdPerRubyToken,
        tokensPerCredit,
        tokensPerUsd: Math.round(tokensPerCredit / exports.USD_PER_STORED_CREDIT),
        usdPerCredit: exports.USD_PER_STORED_CREDIT,
        defaultMultiplier: validateDefaultMultiplier(m).ok ? m : exports.DEFAULT_AI_MULTIPLIER,
        source: perCredit !== null ? 'config' : 'default',
    };
}
exports.AiConfigChangeSchema = zod_1.z.object({
    field: zod_1.z.string(),
    from: zod_1.z.union([zod_1.z.number(), zod_1.z.string(), zod_1.z.boolean(), zod_1.z.null()]),
    to: zod_1.z.union([zod_1.z.number(), zod_1.z.string(), zod_1.z.boolean(), zod_1.z.null()]),
});
exports.AiConfigHistoryEntrySchema = zod_1.z.object({
    at: zod_1.z.string(),
    /** Cognito sub of the super admin who saved. */
    by: zod_1.z.string(),
    byEmail: zod_1.z.string().optional(),
    changes: zod_1.z.array(exports.AiConfigChangeSchema),
});
/** AI registry CONFIG singleton (pk 'AI', sk 'CONFIG'). All fields optional = additive. */
exports.AiRegistryConfigRowSchema = zod_1.z.object({
    pk: zod_1.z.literal('AI'),
    sk: zod_1.z.literal('CONFIG'),
    /** Price of one stored credit, read by the margin/revenue reports. Kept at $0.01. */
    creditUnitPriceUsd: zod_1.z.number().positive().max(100).optional(),
    minPurchaseCredits: zod_1.z.number().int().positive().max(1000000).optional(),
    /** USD value of ONE Ruby Token. 0.01 / x must be a whole number. */
    usdPerRubyToken: zod_1.z.number().refine((x) => tokensPerCreditFor(x) !== null, {
        message: '0.01 / usdPerRubyToken must be a whole number of tokens per credit (≥ 1)',
    }).optional(),
    /** Multiplier used when an action / item has none of its own. */
    defaultMultiplier: zod_1.z.number().positive().max(exports.MAX_AI_MULTIPLIER).optional(),
    history: zod_1.z.array(exports.AiConfigHistoryEntrySchema).max(exports.CONFIG_HISTORY_LIMIT).optional(),
    updatedAt: zod_1.z.string().optional(),
    updatedBy: zod_1.z.string().optional(),
});
/** Fields whose changes are written to the CONFIG row's history. */
exports.AI_CONFIG_TRACKED_FIELDS = [
    'usdPerRubyToken', 'defaultMultiplier', 'creditUnitPriceUsd', 'minPurchaseCredits',
];
const scalar = (v) => v === undefined || v === null ? null
    : typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean' ? v : String(v);
/**
 * The CONFIG row's history after a save: one entry listing each tracked
 * field whose value changed (old → new). No change = the same history.
 * Keeps the newest CONFIG_HISTORY_LIMIT entries, newest last.
 */
function appendConfigHistory(existing, next, who) {
    const prev = Array.isArray(existing?.history) ? existing.history : [];
    const changes = [];
    for (const field of exports.AI_CONFIG_TRACKED_FIELDS) {
        if (!(field in next))
            continue;
        const from = scalar(existing?.[field]);
        const to = scalar(next[field]);
        if (from !== to)
            changes.push({ field, from, to });
    }
    if (changes.length === 0)
        return prev;
    const entry = { at: who.at, by: who.by, changes };
    if (who.byEmail)
        entry.byEmail = who.byEmail;
    return [...prev, entry].slice(-exports.CONFIG_HISTORY_LIMIT);
}
//# sourceMappingURL=tokenEconomics.js.map